import { useMemo, useState } from 'react';
import {
	ComposedChart,
	Line,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer
} from 'recharts';
import { LiquidityScenario } from '@/types/liquidity-api';
import dayjs from 'dayjs';
import { Switch, FormControlLabel } from '@mui/material';
import { filterActiveTransactions } from '@/utils/transactions';
import { useTheme } from '@mui/material/styles';
import Card from '../ui/Card';
interface LiquidityGraphProps {
	scenario: LiquidityScenario;
}

type TooltipEntry = {
	name: string;
	value: number;
	color: string;
};

export const LiquidityGraph = ({ scenario }: LiquidityGraphProps) => {
	const [showTransactions, setShowTransactions] = useState(true);
	const theme = useTheme();

	const chartData = useMemo(() => {
		const dateFrom = dayjs(scenario.dateFrom).startOf('day');
		const dateTo = dayjs(scenario.dateTo).endOf('day');

		// Only include included/pending transactions transactions in the selected date range
		const includedTransactions = filterActiveTransactions(scenario?.transactions || [], dateFrom, dateTo)

		// Ensure start and end dates exist
		const initialState: Record<string, { inflow: number, outflow: number }> = {
			[dateFrom.format('YYYY-MM-DD')]: { inflow: 0, outflow: 0 },
			[dateTo.format('YYYY-MM-DD')]: { inflow: 0, outflow: 0 },
		};
		// Group and aggregate by date
		const dailyData = includedTransactions.reduce((acc, transaction) => {
			const date = dayjs(transaction.transactionDate).format('YYYY-MM-DD');
			if (!acc[date]) {
				acc[date] = { inflow: 0, outflow: 0 };
			}
			if (transaction.amount >= 0) {
				acc[date].inflow += transaction.amount;
			} else {
				acc[date].outflow += transaction.amount;
			}
			return acc;
		}, initialState);

		let runningBalance = scenario.openingBalance || 0;
		return Object.entries(dailyData)
			// Sort by date to ensure proper order in the graph
			.sort(([dateA], [dateB]) => (dayjs(dateA).valueOf() - dayjs(dateB).valueOf()))
			.map(([date, data]) => {
				runningBalance += (data.inflow + data.outflow); // outflow is already negative
				return {
					date,
					inflow: data.inflow,
					outflow: data.outflow,
					balance: runningBalance,
				};
			});
	}, [scenario.dateFrom, scenario.dateTo, scenario.openingBalance, scenario.transactions]);

	const CustomTooltip = ({ active, payload, label }: {
		active?: boolean;
		payload?: TooltipEntry[];
		label?: string;
	}) => {
		if (!active || !payload || !payload.length) return null;
		return (
			<div className="bg-white p-3 border rounded-lg shadow-lg">
				<p className="font-medium">{dayjs(label).format('DD MMM YYYY')}</p>
				{payload.map((entry, index) => (
					<p key={index} style={{ color: entry.color }}>
						{entry.name}: {new Intl.NumberFormat('sv-SE', {
							style: 'currency',
							currency: 'SEK',
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						}).format(entry.value)}
					</p>
				))}
			</div>
		);
	};

	return (
		<Card className="p-4" hoverable={false}>
			<div className="flex items-center justify-end gap-2 mb-4">
				<FormControlLabel
					control={
						<Switch
							checked={showTransactions}
							onChange={(e) => setShowTransactions(e.target.checked)}
						/>
					}
					label="Visa transaktioner"
					labelPlacement="start"
				/>
			</div>

			<ResponsiveContainer width="100%" minWidth={800} height={400}>
				<ComposedChart
					data={chartData}
					margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
				>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis
						dataKey="date"
						tick={{ fontSize: 12 }}
						interval={0}
						tickFormatter={(value) => dayjs(value).format('DD MMM')}
						scale="point"
						padding={{ left: 40, right: 40 }}
					/>
					<YAxis
						tick={{ fontSize: 12 }}
						tickFormatter={(value) => new Intl.NumberFormat('sv-SE', {
							style: 'currency',
							currency: 'SEK',
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						}).format(value)}
						scale="auto"
						domain={['auto', 'auto']}
						exponent={0.5}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Legend />
					{showTransactions && (
						<>
							<Bar
								dataKey="inflow"
								barSize={40}
								name="Inbetalningar"
								fill={theme.palette.chart.income}
								radius={[4, 4, 0, 0]}
								stackId="stack"
							/>
							<Bar
								dataKey="outflow"
								barSize={40}
								name="Utbetalningar"
								fill={theme.palette.chart.expense}
								radius={[4, 4, 0, 0]}
								stackId="stack"
							/>
						</>)}
					<Line
						type="monotone"
						dataKey="balance"
						name="Kassabalans"
						strokeWidth={2}
						stroke={theme.palette.chart.trend}
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</Card>
	);
}; 